import React from 'react';

import {
  Box,
  Flex,
  Stack,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  type StepperProps,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaCircle } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

import { Icon, IconPlay } from '@/components';

type CoachInterviewNavProps = {
  questionCount?: number;
  currentQuestionIndex?: number;
  subStepIndex?: number;
};

export const CoachInterviewNav: React.FC<CoachInterviewNavProps> = (props) => {
  const { t } = useTranslation(['coach', 'interview']);
  const { pathname } = useLocation();
  const steps = [
    { title: t('interview:sampleQuestion'), path: 'sample-question' },
    { title: t('coach:new.overview.interviewQuestions'), path: 'questions' },
    { title: t('coach:new.overview.resultPreview'), path: 'result' },
  ];

  const isLastStep = (activeStep: number) => {
    return activeStep === steps.length - 1;
  };
  const activeStep = steps.findIndex((step) => pathname.includes(step.path));

  const variant = useBreakpointValue<StepperProps['orientation']>({
    base: 'horizontal',
    lg: 'vertical',
  });

  return (
    <Stack
      bg="white"
      borderRadius={12}
      border="1px solid"
      borderColor="gray.50"
    >
      <Stepper
        size="lg"
        orientation={variant}
        borderRadius="lg"
        py={{ base: 2, md: 6 }}
        pb={{ base: 8, lg: 0 }}
        px={{ base: 4, md: 6 }}
        w={{ base: '70%', md: 'full' }}
        gap="0"
        alignItems="start"
        colorScheme="brand"
        index={isLastStep(activeStep) ? activeStep + 1 : activeStep}
      >
        {steps.map((step, index) => (
          <Step key={step.path}>
            <StepIndicator
              position="relative"
              ml={{ base: 2, sm: 0 }}
              sx={{
                '[data-status=complete] &': {
                  background: 'primary.500',
                  borderColor: 'primary.500',
                  width: { base: 7, md: 10 },
                  height: { base: 7, md: 10 },
                },
                '[data-status=incomplete] &': {
                  width: { base: 8, md: 10 },
                  height: { base: 8, md: 10 },
                },
                '[data-status=active] &': {
                  backgroundColor: { base: 'primary.50', md: 'primary.100' },
                  outline: 'solid',
                  outlineColor: { base: 'primary.500', md: 'primary.50' },
                  width: { base: 7, md: 10 },
                  height: { base: 7, md: 10 },
                  outlineWidth: { base: 3, md: 4 },
                  borderWidth: { base: 2, md: 3 },
                  borderColor: { base: 'primary.100', md: 'primary.200' },
                  padding: 0,
                },
              }}
            >
              <StepStatus
                complete={<StepIcon />}
                incomplete={
                  <Icon
                    color="gray.200"
                    icon={FaCircle}
                    fontSize={{ base: 11, md: 16 }}
                  />
                }
                active={
                  <IconPlay
                    color="primary.500"
                    ml={1}
                    fontSize={{ base: 11, md: 16 }}
                  />
                }
              />
              <Stack
                display={{ md: 'none' }}
                top={8}
                left={-1}
                position="absolute"
              >
                <Text
                  color={activeStep >= index ? 'brand.500' : 'gray.600'}
                  fontWeight={500}
                  whiteSpace="nowrap"
                  fontSize={14}
                >
                  {step.title}
                </Text>
              </Stack>
            </StepIndicator>

            <Stack display={{ base: 'none', md: 'block' }}>
              <Flex
                justifyContent="center"
                alignItems="center"
                h="var(--stepper-indicator-size)"
              >
                <StepTitle
                  as={Text}
                  fontSize={{ base: 16, md: 18 }}
                  ml={2}
                  textColor={activeStep === index ? 'brand.500' : 'black'}
                >
                  {step.title}
                </StepTitle>
              </Flex>
              <StepDescription
                display={{ base: 'none', lg: 'block' }}
                py={4}
                px={2}
              >
                {pathname.includes('questions') &&
                  props.subStepIndex === index && (
                    <SubStep
                      currentIndex={props.currentQuestionIndex}
                      questionCount={props.questionCount}
                    />
                  )}
              </StepDescription>
            </Stack>
            <StepSeparator />
          </Step>
        ))}
      </Stepper>
    </Stack>
  );
};

type SubStepType = {
  currentIndex?: number;
  questionCount?: number;
};
const SubStep: React.FC<SubStepType> = ({ questionCount, currentIndex }) => {
  const { t } = useTranslation(['common']);
  if (!questionCount && typeof questionCount !== 'number') return;
  if (!currentIndex && typeof currentIndex !== 'number') return;

  type questionType = { id: number; title: string };
  const questions: Array<questionType> = Array.from(
    { length: questionCount },
    (_, index) => ({
      id: index,
      title: t('common:detail.question') + ' ' + (index + 1).toString(),
    })
  );

  return (
    <Stepper index={currentIndex ?? 0} gap={0} orientation="vertical">
      {questions.map((step, index) => (
        <Stack spacing={2} key={step.id}>
          <Step>
            <StepIndicator
              width={6}
              height={6}
              sx={{
                '[data-status=active] &': {
                  backgroundColor: 'primary.50',
                  outline: 'solid',
                  outlineColor: 'primary.500',
                  outlineWidth: 3,
                  borderWidth: 3,
                  borderColor: 'primary.100',
                  padding: 0,
                },
                '[data-status=complete] &': {
                  borderColor: 'primary.500',
                  backgroundColor: 'primary.100',
                  borderWidth: 2,
                  outlineWidth: 0,
                },
                '[data-status=incomplete] &': {
                  backgroundColor: 'white',
                  outlineColor: 'gray.100',
                  outlineWidth: 3,
                  borderWidth: 0,
                },
              }}
            >
              <StepStatus
                complete={<StepIcon color="primary.500" />}
                incomplete={
                  <Icon color="gray.200" icon={FaCircle} fontSize={9} />
                }
                active={<IconPlay color="primary.500" ml={0.5} fontSize={9} />}
              />
            </StepIndicator>

            <StepTitle fontSize={14}>{step.title}</StepTitle>
          </Step>
          {questions.length !== index + 1 && (
            <Box
              ml={2.5}
              width={0.5}
              height={5}
              mt={-1}
              mb={1}
              backgroundColor={
                currentIndex >= index ? 'primary.300' : 'gray.100'
              }
            />
          )}
        </Stack>
      ))}
    </Stepper>
  );
};
